import { useEffect, useMemo, useState } from 'react';
import { IProof } from 'src/ts/interfaces';
import { isUnverifiedOfferLetter, OFFER_LETTER_TYPE } from '../helpers/proofHelpers';

interface UseDocumentNavigationProps {
  proofs: IProof[];
}

interface UseDocumentNavigationReturn {
  selectedProofId: string;
  setSelectedProofId: (id: string) => void;
  currentProof: IProof | undefined;
  manuallyReviewableProofs: IProof[];
  currentNavigableIndex: number;
  currentManualIndex: number;
  manualProofCount: number;
  isFirstDocument: boolean;
  isLastDocument: boolean;
  canNavigateBack: boolean;
  canNavigateForward: boolean;
  handleNextDocument: () => void;
  handlePreviousDocument: () => void;
}

export const useDocumentNavigation = ({
  proofs
}: UseDocumentNavigationProps): UseDocumentNavigationReturn => {
  const [selectedProofId, setSelectedProofId] = useState('');

  const currentProof = proofs.find((proof: IProof) => proof.id === selectedProofId);
  const currentNavigableIndex = proofs.findIndex((proof: IProof) => proof.id === selectedProofId);

  const manuallyReviewableProofs = useMemo(
    () =>
      proofs
        // ITL-2690: exclude unverified offer letters from the navigable list,
        // prev/next, and "Document X of Y" indicator. Per product guidance, these
        // proofs don't carry `is_automatic_review`, so we need the explicit check.
        .filter((proof) => !proof.is_automatic_review && !isUnverifiedOfferLetter(proof))
        .sort((a, b) => {
          const aIsOL = (a.type as string) === OFFER_LETTER_TYPE ? 1 : 0;
          const bIsOL = (b.type as string) === OFFER_LETTER_TYPE ? 1 : 0;
          return aIsOL - bIsOL;
        }),
    [proofs]
  );
  const currentManualIndex = manuallyReviewableProofs.findIndex(
    (proof) => proof.id === selectedProofId
  );
  const isOnManuallyReviewableDoc = currentManualIndex >= 0;

  const isLastDocument = isOnManuallyReviewableDoc
    ? currentManualIndex === manuallyReviewableProofs.length - 1
    : currentNavigableIndex === proofs.length - 1;
  const isFirstDocument = isOnManuallyReviewableDoc
    ? currentManualIndex === 0
    : currentNavigableIndex === 0;

  const canNavigateBack = (() => {
    if (isOnManuallyReviewableDoc) return currentManualIndex > 0;
    return manuallyReviewableProofs.some(
      (proof) => proofs.findIndex((p) => p.id === proof.id) < currentNavigableIndex
    );
  })();

  const canNavigateForward = (() => {
    if (isOnManuallyReviewableDoc) return currentManualIndex < manuallyReviewableProofs.length - 1;
    return manuallyReviewableProofs.some(
      (proof) => proofs.findIndex((p) => p.id === proof.id) > currentNavigableIndex
    );
  })();

  useEffect(() => {
    if (proofs.length > 0 && !selectedProofId) {
      const firstManualProof = manuallyReviewableProofs[0];
      if (firstManualProof) {
        setSelectedProofId(firstManualProof.id);
      } else {
        setSelectedProofId(proofs[0].id);
      }
    }
  }, [proofs, selectedProofId, manuallyReviewableProofs]);

  useEffect(() => {
    const isCurrentProofValid =
      selectedProofId && proofs.some((proof) => proof.id === selectedProofId);

    if (proofs.length > 0 && selectedProofId && !isCurrentProofValid) {
      const firstManualProof = manuallyReviewableProofs[0];
      if (firstManualProof) {
        setSelectedProofId(firstManualProof.id);
      } else {
        setSelectedProofId(proofs[0].id);
      }
    }
  }, [selectedProofId, proofs, manuallyReviewableProofs]);

  const handleNextDocument = () => {
    if (isOnManuallyReviewableDoc && currentManualIndex < manuallyReviewableProofs.length - 1) {
      const nextManualIndex = currentManualIndex + 1;
      setSelectedProofId(manuallyReviewableProofs[nextManualIndex].id);
    } else if (!isOnManuallyReviewableDoc) {
      const nextManualProof = manuallyReviewableProofs.find(
        (proof) => proofs.findIndex((p) => p.id === proof.id) > currentNavigableIndex
      );
      if (nextManualProof) {
        setSelectedProofId(nextManualProof.id);
      }
    }
  };

  const handlePreviousDocument = () => {
    if (isOnManuallyReviewableDoc && currentManualIndex > 0) {
      const prevManualIndex = currentManualIndex - 1;
      setSelectedProofId(manuallyReviewableProofs[prevManualIndex].id);
    } else if (!isOnManuallyReviewableDoc) {
      const prevManualProof = manuallyReviewableProofs
        .slice()
        .reverse()
        .find((proof) => proofs.findIndex((p) => p.id === proof.id) < currentNavigableIndex);
      if (prevManualProof) {
        setSelectedProofId(prevManualProof.id);
      }
    }
  };

  return {
    selectedProofId,
    setSelectedProofId,
    currentProof,
    currentNavigableIndex,
    currentManualIndex,
    manuallyReviewableProofs,
    manualProofCount: manuallyReviewableProofs.length,
    isFirstDocument,
    isLastDocument,
    canNavigateBack,
    canNavigateForward,
    handleNextDocument,
    handlePreviousDocument
  };
};
